import type { Attachment } from '@medplum/fhirtypes'
import type { ValidatedTool } from '@seen/core/models/validatedTool'
import type { ValidatedToolResponse } from '@seen/core/models/validatedToolResponse'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import type { FieldPath, PathValue } from 'react-hook-form'
import type { z } from 'zod'
import type { ValidatedToolSetValue } from '#src/components/validated-tool-form/shared/validatedToolSuggestion.ts'
import {
  type FormSchema,
  type UseFormReturn,
  useForm,
} from '#src/hooks/useForm.ts'

// A field the prefill may fill: never written, or written and then cleared.
function isEmptyAnswer<T>(value: T): boolean {
  return value === null || value === undefined || value === ''
}

export interface UseValidatedToolFormProps<TSchema extends FormSchema> {
  schema: TSchema
  validatedTool: ValidatedTool
  response?: ValidatedToolResponse
  /**
   * AI-staged answer values keyed by linkId, used to pre-populate an UNSUBMITTED
   * form. Unlike `response`, this does NOT disable the form or hide the submit
   * button — the clinician reviews and edits the suggestion, then submits to
   * create the QuestionnaireResponse via the normal path. Ignored when a
   * `response` is present (an already-submitted tool wins).
   */
  prefillValuesByLinkId?: Record<string, string>
  disabled?: boolean
  restorationId?: string
}

/* A validated-tool form, plus the one extra thing an AI suggestion needs of it.
 * An INTERSECTION rather than a new shape, so every existing `const form =
 * useValidatedToolForm(...)` call site is untouched. */
export type UseValidatedToolFormReturn<TSchema extends FormSchema> =
  UseFormReturn<TSchema> & {
    /* Write one field's value on behalf of a suggestion decision. Lives here
     * rather than in each of the twenty forms, which all needed the identical
     * linkId-to-FieldPath cast. */
    setSuggestionValue: ValidatedToolSetValue
  }

export function useValidatedToolForm<TSchema extends FormSchema>({
  schema,
  validatedTool,
  response,
  prefillValuesByLinkId,
  disabled,
  restorationId,
}: UseValidatedToolFormProps<TSchema>): UseValidatedToolFormReturn<TSchema> {
  const defaultValues = useMemo(() => {
    const items = validatedTool.formItems
    return items.reduce<
      Record<string, string | number | boolean | Attachment | null>
    >((acc, item) => {
      const submittedValue = response?.getAnswerValue(item.linkId)
      const prefillValue = response
        ? undefined
        : prefillValuesByLinkId?.[item.linkId]
      acc[item.linkId] = submittedValue ?? prefillValue ?? null
      return acc
    }, {})
  }, [validatedTool, response, prefillValuesByLinkId])

  const form = useForm<TSchema>({
    schema,
    defaultValues,
    // A staged prefill must keep the form editable — only a persisted `response`
    // (or an explicit `disabled`) locks it.
    disabled: disabled ?? !!response,
    restorationId,
  })

  /* Stable content key for the staged prefill so a note re-fetch that yields a
   * NEW object with the SAME values does not retrigger the reset below and wipe
   * the clinician's in-progress edits. Reference identity would churn on every
   * background refetch; the serialized content only changes when the suggestion
   * actually changes. */
  const prefillKey = useMemo(
    () =>
      prefillValuesByLinkId
        ? JSON.stringify(
            Object.entries(prefillValuesByLinkId).sort(([a], [b]) =>
              a.localeCompare(b)
            )
          )
        : null,
    [prefillValuesByLinkId]
  )

  const formReset = form.reset
  const formGetValues = form.getValues
  const setFormValue = form.setValue
  const prevPrefillRef = useRef<Readonly<Record<string, string>>>({})
  // Reset to defaults when a response loads (submitted tool), when a staged
  // prefill first arrives / its content changes (unsubmitted form), or when a
  // prefill is WITHDRAWN (rejected suggestion: it stops carrying any value)
  // — otherwise the rejected AI values would linger in the editable form and a
  // submit would commit them as the clinician's own. Keyed on `prefillKey`
  // (content) rather than the recomputed `defaultValues` reference, so a note
  // re-fetch with unchanged values doesn't clobber clinician edits; the
  // transition check keeps a never-prefilled blank form from resetting away
  // manual edits.
  useEffect(() => {
    /* What the prefill carried last time. A field's value arriving for the
     * FIRST time, which on a note that loaded with pre-existing staging
     * happens long after mount: the review starts deferred behind "Review
     * suggestions", so the pending values only arrive when the clinician
     * reveals. And a field leaving it, which hiding the review does to every
     * pending one. */
    const prevPrefill = prevPrefillRef.current
    prevPrefillRef.current = prefillValuesByLinkId ?? {}
    /* An empty prefill carries nothing, so it neither seeds nor withdraws:
     * a record staged while its review is hidden, or one that goes away with
     * nothing seeded, must not reset away answers the draft store restored,
     * which the form holds as defaults and so never reads as dirty. */
    const carriesPrefill = Object.keys(prefillValuesByLinkId ?? {}).length > 0
    const prefillWithdrawn =
      !carriesPrefill && Object.keys(prevPrefill).length > 0
    if (response || carriesPrefill || prefillWithdrawn) {
      // keepDirtyValues preserves fields the clinician has actually TOUCHED
      // (manual edits, and per-field accepts — which setValue marks dirty) while
      // still applying prefill changes to UNtouched (AI-seeded) fields. Critical
      // now that a single field's reject removes one linkId from the prefill and
      // retriggers this reset: without it, rejecting one suggestion would wipe a
      // typed summary or a sibling field the clinician had already set. A
      // never-dirtied prefilled field still resets to its (possibly blank) new
      // default, so the "withdraw clears the form" behavior is preserved.
      // Skipped when a `response` loads — a submitted tool is the source of
      // truth and must reset the form fully.
      formReset(defaultValues, response ? undefined : { keepDirtyValues: true })
      /* Nor can it be trusted to withdraw one, for the same reason: the radio
       * a pending suggestion seeded reads as dirty, so the reset keeps it and
       * the field goes on answering with the AI's value after the prefill
       * stopped carrying it. Hiding the review did exactly that: highlights
       * gone, every pending answer still selected and scored, and a Submit
       * under them. So a field leaving a prefill that is still in force is
       * cleared here when it still holds the value the prefill gave it,
       * leaving it as a reload would show it. A field the clinician answered
       * differently is theirs and stays; one they answered with the same
       * value cannot be told apart from the seed, and clears with it. A
       * prefill withdrawn outright is left to the reset: that is a submit
       * whose response is still loading, and clearing there would blank the
       * answers just submitted until it arrives. */
      if (prefillKey) {
        for (const [linkId, value] of Object.entries(prevPrefill)) {
          const path = linkId as FieldPath<z.infer<TSchema>>
          if (
            !Object.hasOwn(prefillValuesByLinkId ?? {}, linkId) &&
            formGetValues(path) === value
          ) {
            setFormValue(
              path,
              null as PathValue<z.infer<TSchema>, FieldPath<z.infer<TSchema>>>
            )
          }
        }
      }
      /* `keepDirtyValues` cannot be trusted to apply a first prefill. By the
       * time it arrives every radio field is already marked dirty — the field's
       * own registration churn, not a clinician edit — so the reset keeps the
       * blanks it finds and the suggested options render highlighted but
       * UNSELECTED, with a score of zero. That is what a reloaded note showed:
       * six green options, nothing chosen, nothing to explain it.
       *
       * So a field's first prefill is applied field by field, and only where
       * the field is still EMPTY. That fills the blanks without the dirty flags
       * having to be right about anything, and an answer the clinician typed
       * before revealing is not overwritten by a suggestion for the same field.
       * "First" is per field, not per prefill: an unrevealed note already
       * prefills its accepted fields, so revealing ADDS the pending ones to a
       * prefill that is not new. Fields the prefill already carried keep going
       * through the reset above: by then the dirty flags distinguish real
       * edits, and a decision that blanked a field must not be undone by
       * re-seeding it. */
      if (prefillKey) {
        for (const [linkId, value] of Object.entries(
          prefillValuesByLinkId ?? {}
        )) {
          if (
            !Object.hasOwn(prevPrefill, linkId) &&
            isEmptyAnswer(formGetValues(linkId as FieldPath<z.infer<TSchema>>))
          ) {
            setFormValue(
              linkId as FieldPath<z.infer<TSchema>>,
              value as PathValue<z.infer<TSchema>, FieldPath<z.infer<TSchema>>>
            )
          }
        }
      }
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- intentionally keyed on stable prefillKey, not the defaultValues reference
  }, [formReset, response, prefillKey])

  /* Applied as a clinician edit (validate + dirty) so the score recomputes and
   * the value survives the prefill-reset effect. Dirtying matters just as much
   * for a BLANK: a reject leaves the field unanswered, and an undirtied blank is re-seeded by the next `keepDirtyValues` reset — which is
   * how a rejected field kept showing the answer it had just refused.
   *
   * The node view supplies AI answer linkIds and values that are dynamic from
   * the form's perspective; they correspond to this schema's fields, so the
   * cast to the form's path/value types happens here, once, at that boundary.
   */
  const formSetValue = form.setValue
  const setSuggestionValue = useCallback<ValidatedToolSetValue>(
    (linkId, value) => {
      type Values = z.infer<TSchema>
      formSetValue(
        linkId as FieldPath<Values>,
        value as PathValue<Values, FieldPath<Values>>,
        { shouldValidate: true, shouldDirty: true }
      )
    },
    [formSetValue]
  )

  /* Attached to react-hook-form's OWN object rather than spread into a new one:
   * RHF reassigns `formState` on that object every render, so a copy would
   * freeze the form state at the moment the copy was made. */
  const validatedToolForm = form as UseValidatedToolFormReturn<TSchema>
  validatedToolForm.setSuggestionValue = setSuggestionValue
  return validatedToolForm
}
